// Resolves a model row into a ModelSource — the routing input for ModelViewer.
// Centralizes the modelUrl / audioUrl / sequenceInfo derivation that was
// previously copy-pasted across Gallery / ShareViewer / Technology /
// ThumbnailGenerator.

export interface SequenceInfo {
  frameUrls: string[]
  fps: number
  audioUrl?: string | null
}

/** 'single' .splatv / .splat, 'ply' one .ply, 'sequence' .4dms / .plyseq
 *  frames, 'clipgstream' ClipGStream dataset, 'fourdv' one .4dv (4D Gaussians). */
export type ModelSourceKind = 'single' | 'ply' | 'sequence' | 'clipgstream' | 'fourdv'

/** Pose of one layer inside its parent. Scale is a scalar because Spark
 *  averages a decomposed non-uniform scale, so only uniform scale renders as
 *  authored. Quaternion is in three's (x, y, z, w) order. */
export interface LayerTransform {
  position: { x: number; y: number; z: number }
  quaternion: { x: number; y: number; z: number; w: number }
  scale: number
}

export interface ModelSource {
  kind: ModelSourceKind
  /** Entry-file URL (original.splatv / original.ply / original.4dv / manifest.json). */
  modelUrl: string
  /** audio.mp3 beside the entry file (single-file playback; sequences carry
   *  their own URL in sequenceInfo, ClipGStream loads it from dataUrl). */
  audioUrl: string | null
  sequenceInfo: SequenceInfo | null
  /** ClipGStream dataset root (contains manifest.json / base/ / clipNNN/). */
  dataUrl: string | null
  /** Persistent scene 3DGS shown alongside the playing sequence, or null.
   *  Top-level rather than inside sequenceInfo so a single .ply model can carry
   *  one too (sequenceInfo is null for kind 'ply'). */
  staticUrl: string | null
  /** Pose of the scene layer inside the performer's frame. null = identity,
   *  which is correct when both were solved from the same COLMAP sparse/0. */
  staticTransform: LayerTransform | null
  /** Clip range in seconds of a .4dv (metadata.fourdv.timeRange, measured by scan4dv at upload),
   *  or null. A large .4dv is streamed in time segments and plans them from this range. */
  timeRange?: { start: number; end: number } | null
  /** Gaussian count of a .4dv (metadata.fourdv.gaussianCount, from scan4dv at upload), or null.
   *  Lets the viewer apply the phone / headset policy (stream, no XR above 2.5M) before the file
   *  is opened; without it the viewer uses the count read from the file. */
  gaussianCount?: number | null
}

// Free-form JSON from the DB — every field is verified before use.
interface ModelMetadata {
  format?: unknown
  clipgstream?: { dataPrefix?: unknown }
  sequence?: {
    frameIds?: unknown
    framesPrefix?: unknown
    frameFormat?: unknown
    manifestPath?: unknown
    fps?: unknown
    /** R2 key of the persistent scene 3DGS, written at upload. */
    staticPath?: unknown
  }
  /** Authored pose of the scene layer. Top-level (not under `sequence`) because
   *  it is edited repeatedly in the admin editor, like defaultCamera /
   *  modelTransform / showcaseAnimation, whereas `sequence` is a one-time copy
   *  of the R2 manifest made when the model row is created. */
  staticLayer?: { transform?: unknown }
  /** .4dv stats written by the server from the upload (scan4dv). */
  fourdv?: { timeRange?: unknown; gaussianCount?: unknown }
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

/** Validate a persisted transform. Returns null for anything unusable so the
 *  viewer falls back to identity rather than rendering at a garbage pose. */
function parseLayerTransform(raw: unknown): LayerTransform | null {
  if (!raw || typeof raw !== 'object') return null
  const t = raw as Record<string, unknown>
  const p = (t.position && typeof t.position === 'object' ? t.position : {}) as Record<string, unknown>
  const q = (t.quaternion && typeof t.quaternion === 'object' ? t.quaternion : {}) as Record<string, unknown>
  const out: LayerTransform = {
    position: { x: num(p.x, 0), y: num(p.y, 0), z: num(p.z, 0) },
    quaternion: { x: num(q.x, 0), y: num(q.y, 0), z: num(q.z, 0), w: num(q.w, 1) },
    scale: num(t.scale, 1),
  }
  if (out.scale <= 0) out.scale = 1
  const { x, y, z, w } = out.quaternion
  if (x * x + y * y + z * z + w * w < 1e-12) out.quaternion = { x: 0, y: 0, z: 0, w: 1 }
  return out
}

/** A usable clip range: finite seconds with end > start, else null. */
function parseTimeRange(raw: unknown): { start: number; end: number } | null {
  if (!raw || typeof raw !== 'object') return null
  const { start, end } = raw as { start?: unknown; end?: unknown }
  if (typeof start !== 'number' || typeof end !== 'number') return null
  if (!Number.isFinite(start) || !Number.isFinite(end) || !(end > start)) return null
  return { start, end }
}

/** A usable gaussian count: a positive integer, else null. */
function parseCount(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isInteger(raw) && raw > 0 ? raw : null
}

function parseMetadata(raw: unknown): ModelMetadata | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as ModelMetadata } catch { return null }
  }
  return raw as ModelMetadata
}

export function resolveModelSource(model: {
  id?: string
  filePath?: string | null
  file_path?: string | null
  metadata?: unknown
}): ModelSource {
  const baseUrl = import.meta.env.VITE_R2_PUBLIC_URL || 'https://4dgs-models.moonshine-studio.net'
  const fp = model.filePath ?? model.file_path ?? ''
  // Local dev serves single files through the stream endpoint (.4dv excepted, see below).
  const modelUrl = fp
    ? (baseUrl.includes('localhost') && model.id ? `${baseUrl}/${model.id}/stream` : `${baseUrl}/${fp}`)
    : ''
  const audioUrl = fp ? `${baseUrl}/${fp.replace(/[^/]+$/, 'audio.mp3')}` : null

  const meta = parseMetadata(model.metadata)

  // Persistent scene 3DGS. The R2 key is written into metadata.sequence at
  // upload; the pose is authored later in the admin editor and lives at the
  // metadata top level.
  const staticPath = typeof meta?.sequence?.staticPath === 'string' ? meta.sequence.staticPath : ''
  const staticUrl = staticPath ? `${baseUrl}/${staticPath.replace(/^\//, '')}` : null
  const staticTransform = parseLayerTransform(meta?.staticLayer?.transform)

  // ClipGStream dataset (zip upload): entry file is the dataset's manifest.json.
  if (meta?.format === 'clipgstream') {
    const prefix = (
      typeof meta.clipgstream?.dataPrefix === 'string'
        ? meta.clipgstream.dataPrefix
        : fp.replace(/manifest\.json$/i, '')
    ).replace(/\/$/, '')
    return {
      kind: 'clipgstream', modelUrl, audioUrl, sequenceInfo: null,
      dataUrl: `${baseUrl}/${prefix}`, staticUrl: null, staticTransform: null,
    }
  }

  // .4dms / .plyseq sequence: per-frame URLs from the manifest metadata.
  if (meta?.format === 'plyseq-v2' && meta.sequence) {
    const seq = meta.sequence
    const ids = Array.isArray(seq.frameIds) ? (seq.frameIds as number[]) : []
    if (ids.length > 0) {
      const prefix = (typeof seq.framesPrefix === 'string' ? seq.framesPrefix : '').replace(/\/$/, '')
      const frameExt = seq.frameFormat === 'spz' ? 'spz' : 'ply'
      const frameUrls = ids.map((id) => `${baseUrl}/${prefix}/frame_${id}.${frameExt}`)
      // Audio sits beside manifest.json in R2. If the upload never had one the
      // request 404s and the <audio> element fails silently.
      const manifestPath = typeof seq.manifestPath === 'string' ? seq.manifestPath : null
      const audioPath = manifestPath
        ? manifestPath.replace(/[^/]+$/, 'audio.mp3')
        : `${prefix.replace(/\/?frames$/, '')}/audio.mp3`
      return {
        kind: 'sequence',
        modelUrl,
        audioUrl,
        sequenceInfo: {
          frameUrls,
          fps: typeof seq.fps === 'number' && seq.fps > 0 ? seq.fps : 30,
          audioUrl: `${baseUrl}/${audioPath}`,
        },
        dataUrl: null,
        staticUrl,
        staticTransform,
      }
    }
  }

  // .4dv: one self-contained 4D Gaussian file. The server stamps format '4dv'
  // at upload; the extension is the fallback when metadata lacks it. The URL
  // is the R2 key in dev too: the dev passthrough (/models/:uploadId/*) has
  // Range and no visibility gate, unlike /:id/stream. No scene layer.
  if (meta?.format === '4dv' || fp.toLowerCase().endsWith('.4dv')) {
    return {
      kind: 'fourdv', modelUrl: fp ? `${baseUrl}/${fp}` : '', audioUrl, sequenceInfo: null,
      dataUrl: null, staticUrl: null, staticTransform: null,
      timeRange: parseTimeRange(meta?.fourdv?.timeRange),
      gaussianCount: parseCount(meta?.fourdv?.gaussianCount),
    }
  }

  if (fp.toLowerCase().endsWith('.ply')) {
    return { kind: 'ply', modelUrl, audioUrl, sequenceInfo: null, dataUrl: null, staticUrl, staticTransform }
  }
  // SplatViewer (kind 'single') has no static-layer support, so do not advertise one.
  return { kind: 'single', modelUrl, audioUrl, sequenceInfo: null, dataUrl: null, staticUrl: null, staticTransform: null }
}
